import { defineStore } from 'pinia'
import {  i18n } from '@/core/i18n'
import { saveLocale, type Locale } from '@/utils/locale'
import { useCtaStore } from '@/stores/cta/store'
import { useDevicesStore } from '@/stores/devices/store'
import { useDeviceStore } from '@/stores/device/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useExtensionStore } from '@/stores/extension/store'
import { useOfficeDesksStore } from '@/stores/officeDesks/store'
import { useOfficeDeskStore } from '@/stores/officeDesk/store'
import { useTutorialsStore } from '@/stores/tutorials/store'
import { useTariffsStore } from '@/stores/tariffs/store'
import * as Api from './api'
import { useAuthStore } from '@/stores/auth/store'
import { useCallflowStore } from '@/stores/callflow/store'
import { useBackupsStore } from '@/stores/backups/store'
import { usePromptsStore } from '@/stores/prompts/store'
import { useAudioFileStore } from '@/stores/audioFile/store'
import { useContactsStore } from '@/stores/contacts/store'
import { useFeaturesStore } from '@/stores/features/store'
import { callWithMeta, createMeta, waitFor, type Meta } from '@/utils/store'
import { useNavigationStore } from '../navigation/store'
import { setupNpsSurvey } from '@/utils/npsSurvey'

type State = {
  maintenanceMessages?: Api.MaintenanceMessage[]
  provisioningTemplates?: any[]
  meta: Meta<{
    maintenanceMessages: undefined
    loadMaintenanceMessages: undefined
    maintenanceTranslations: undefined
    loadMaintenanceTranslations: undefined
    provisioningTemplates: undefined
    loadProvisioningTemplates: undefined
    translations: undefined
    loadTranslations: undefined
    navigationTranslations: undefined
    loadNavigationTranslations: undefined
    setupNpsSurvey: undefined
  }>
}

const getDefaultState = (): State => ({
  maintenanceMessages: undefined,
  provisioningTemplates: undefined,
  meta: createMeta([
    'maintenanceMessages',
    'loadMaintenanceMessages',
    'maintenanceTranslations',
    'loadMaintenanceTranslations',
    'provisioningTemplates',
    'loadProvisioningTemplates',
    'translations',
    'loadTranslations',
    'navigationTranslations',
    'loadNavigationTranslations',
    'setupNpsSurvey',
  ]),
})

export const useCoreStore = defineStore('core', {
  state: getDefaultState,

  actions: {
    changeLanguage(locale: Locale) {
      saveLocale(locale)
      i18n.global.locale.value = locale
    },

    async validate(data: any) {
      const result = await Api.validate(data)
      return result
    },

    //from old store/modules/cta/index.js
    resetAll() {
      useCtaStore().RESET()
      useExtensionStore().RESET()
      useExtensionsStore().RESET()
      useDeviceStore().RESET()
      useDevicesStore().RESET()
      useOfficeDeskStore().RESET()
      useOfficeDesksStore().RESET()
      usePromptsStore().RESET()
      useAudioFileStore().RESET()
      useTutorialsStore().RESET()
      useTariffsStore().RESET()
      useCallflowStore().RESET()
      useBackupsStore().RESET()
      useContactsStore().RESET()
      useFeaturesStore().RESET()
    },

    async loadAll() {
      if (!useAuthStore().ctaId) {
        await useCtaStore().load()
      }

      await Promise.all([
        (async (): Promise<void> => {
          await useCtaStore().load()

          if (useCtaStore().ctaData.edition !== 'basic') {
            if (
              !useAuthStore().isOfficeDesk ||
              useAuthStore().getHasOfficeDeskPermission('contactsPermission')
            ) {
              await useContactsStore().load()
            }
          }
        })(),
        useExtensionStore().reload(),
        useExtensionsStore().load(),
        useDeviceStore().reload(),
        useDevicesStore().load(),
        useOfficeDeskStore().reload(),
        useOfficeDesksStore().load(),
        usePromptsStore().load(),
        useAudioFileStore().reload(),
        useTutorialsStore().loadTutorials(),
        useTariffsStore().loadTariffs(),
        useCallflowStore().reload(),
        useBackupsStore().load(),
        useFeaturesStore().load(),
        useNavigationStore().load(),
      ])
    },

    async reloadAll() {
      this.resetAll()
      await this.loadAll()
    },

    async loadMaintenanceMessages({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.maintenanceMessages.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.maintenanceMessages.status !== 'pending')

        return this.maintenanceMessages!
      }

      const { result } = await callWithMeta({
        store: this,
        metaFields: {
          loadMaintenanceMessages: undefined,
          maintenanceMessages: undefined,
        },
        apiCall: async () => await Api.loadMaintenanceMessages(),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('maintenanceMessages')) {
            this.maintenanceMessages = result.payload
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async loadMaintenanceTranslations({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.maintenanceTranslations.status
      )

      if (useCache && hasCached) {
        await waitFor(
          () => this.meta.maintenanceTranslations.status !== 'pending'
        )

        return
      }

      const { result } = await callWithMeta({
        apiCall: async () => await Api.loadMaintenanceTranslations(),
        store: this,
        metaFields: {
          loadMaintenanceTranslations: undefined,
          maintenanceTranslations: undefined,
        },
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('maintenanceTranslations')) {
            Object.entries(result.payload).forEach(
              ([languageKey, translations]) => {
                i18n.global.mergeLocaleMessage(languageKey, translations ?? {})
              }
            )
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async loadProvisioningTemplates({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.provisioningTemplates.status
      )

      if (useCache && hasCached) {
        await waitFor(
          () => this.meta.provisioningTemplates.status !== 'pending'
        )

        return
      }

      const { result } = await callWithMeta({
        store: this,
        metaFields: {
          loadProvisioningTemplates: undefined,
          provisioningTemplates: undefined,
        },
        apiCall: async () => await Api.loadProvisioningTemplates(),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('provisioningTemplates')) {
            this.provisioningTemplates = result.payload
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async loadTranslations({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.translations.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.translations.status !== 'pending')
        return
      }

      await callWithMeta({
        apiCall: async () => await Api.loadTranslations(),
        store: this,
        metaFields: {
          translations: undefined,
          loadTranslations: undefined,
        },
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('translations')) {
            Object.entries(result.payload).forEach(
              ([languageKey, translations]) => {
                i18n.global.mergeLocaleMessage(languageKey, translations ?? {})
              }
            )
          }
        },
      })
    },

    async loadNavigationTranslations({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.navigationTranslations.status
      )

      if (useCache && hasCached) {
        await waitFor(
          () => this.meta.navigationTranslations.status !== 'pending'
        )
        return
      }

      await callWithMeta({
        apiCall: async () => await Api.loadNavigationTranslations(),
        store: this,
        metaFields: {
          navigationTranslations: undefined,
          loadNavigationTranslations: undefined,
        },
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('navigationTranslations')) {
            Object.entries(result.payload).forEach(
              ([languageKey, translations]) => {
                i18n.global.mergeLocaleMessage(languageKey, translations ?? {})
              }
            )
          }
        },
      })
    },

    async setupNpsSurvey() {
      // Only set up survey once
      if (this.meta.setupNpsSurvey.status !== 'initial') {
        return
      }

      await callWithMeta({
        apiCall: async () => {
          // Wait for translations and unleash to be loaded
          await Promise.all([
            waitFor(() => this.meta.loadTranslations.status === 'success'),
            waitFor(() => useFeaturesStore().meta.load.status === 'success'),
          ])

          return { status: 'success', payload: undefined }
        },
        store: this,
        metaFields: {
          setupNpsSurvey: undefined,
        },
        onSuccess: () => {
          const npsSurveyUrl = i18n.global.t('npsSurvey.url')

          if (useFeaturesStore().hasNpsSurveyFeature && npsSurveyUrl.length) {
            setupNpsSurvey(npsSurveyUrl)
          }
        },
      })
    },
  },
})
